import React, { useState } from 'react';
import { h, Book, MessageCircle, HelpCircle, Mail, Phone } from 'lucide-react';
import Container from '../../components/ui/Container';

const Help = () => {
  const [formSuccess, setFormSuccess] = useState(false); // Track form submission success
  const [loading, setLoading] = useState(false); // Track loading state

  const faqs = [
    {
      question: 'How do I book a session with a teacher?',
      answer:
        'Browse our teacher listings, select a teacher, and click "Book Session". Choose your preferred time slot and complete the booking process.',
    },
    {
      question: 'What happens if I need to cancel a session?',
      answer:
        'You can cancel a session up to 24 hours before the scheduled time for a full refund. Later cancellations may be subject to our cancellation policy.',
    },
    {
      question: 'How do payments work?',
      answer:
        "We use secure payment processing. You'll be charged when you book a session, and the payment is held until the session is completed.",
    },
    {
      question: "What if I'm not satisfied with a session?",
      answer:
        "We offer a satisfaction guarantee. If you're not happy with a session, contact our support team within 24 hours for assistance.",
    },
  ];

  const onSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    const formData = new FormData(event.target);
    formData.append('access_key', 'bf5655e8-d750-4808-9035-6d847ccf569f');

    const object = Object.fromEntries(formData);
    const json = JSON.stringify(object);

    const res = await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: json,
    }).then((res) => res.json());

    if (res.success) {
      setLoading(false);
      setFormSuccess(true);
      setTimeout(() => {
        setFormSuccess(false); // Hide success message after a few seconds
        event.target.reset(); // Reset form fields
      }, 5000);
    } else {
      setLoading(false);
      alert('Something went wrong! Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-20">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold mb-6">How Can We Help You?</h1>
          </div>
        </Container>

        {/* Quick Links */}
        <section className="py-12 ">
          <Container>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="flex items-center p-6 bg-indigo-50 rounded-lg">
                <Book className="h-8 w-8 text-indigo-600 mr-4" />
                <div>
                  <h3 className="font-semibold mb-1 text-black">
                    Getting Started
                  </h3>
                  <p className="text-sm text-gray-600">
                    New to AcademiaHub? Start here
                  </p>
                </div>
              </div>
              <div className="flex items-center p-6 bg-purple-50 rounded-lg">
                <MessageCircle className="h-8 w-8 text-purple-600 mr-4" />
                <div>
                  <h3 className="font-semibold mb-1 text-black">
                    Common Questions
                  </h3>
                  <p className="text-sm text-gray-600">Find quick answers</p>
                </div>
              </div>
              <div className="flex items-center p-6 bg-blue-50 rounded-lg">
                <HelpCircle className="h-8 w-8 text-blue-600 mr-4" />
                <div>
                  <h3 className="font-semibold mb-1 text-black">
                    Support Center
                  </h3>
                  <p className="text-sm text-gray-600">
                    Detailed guides and tutorials
                  </p>
                </div>
              </div>
            </div>
          </Container>
        </section>
      </section>

      {/* FAQs */}
      <section className="py-16">
        <Container>
          <h2 className="text-3xl font-bold mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Contact Form */}
      <section className="py-2 bg-gray-50">
        <Container>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">
              Need Assistance? Reach Out to Us Anytime!
            </h2>
            <form
              className="bg-white p-8 rounded-lg shadow-sm space-y-6"
              onSubmit={onSubmit}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <input
                  type="text"
                  placeholder="First Name"
                  name="name"
                  className="border border-gray-300 p-3 rounded-lg focus:outline-none focus:border-indigo-500"
                />
                <input
                  type="text"
                  placeholder="Last Name"
                  name="last_name"
                  className="border border-gray-300 p-3 rounded-lg focus:outline-none focus:border-indigo-500"
                />
              </div>
              <input
                type="email"
                placeholder="Email Address"
                name="email"
                className="w-full border border-gray-300 p-3 rounded-lg focus:outline-none focus:border-indigo-500"
              />
              <textarea
                placeholder="Your Message"
                name="message"
                className="w-full border border-gray-300 p-3 rounded-lg focus:outline-none focus:border-indigo-500 h-32"
              />
              <button
                type="submit"
                className="w-full bg-indigo-600 text-white p-3 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                {loading ? 'Sending...' : 'Submit'}
              </button>
            </form>
            {formSuccess && (
              <div className="mt-6 text-center bg-green-100 text-green-800 p-4 rounded-lg">
                <h3 className="text-lg font-semibold">
                  Message Sent Successfully!
                </h3>
                <p>Thank you for reaching out. We will get back to you soon.</p>
              </div>
            )}
          </div>
        </Container>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <Container>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">
              Still Need Help?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="text-center p-8 border border-gray-200 rounded-lg">
                <Mail className="h-8 w-8 mx-auto mb-4 text-indigo-600" />
                <h3 className="font-semibold mb-2">Email Support</h3>
                <p className="text-gray-600 mb-4">
                  Get a response within 24 hours
                </p>
                <a
                  href="mailto:academiahub2025@gmail.com"
                  className="text-indigo-600 hover:text-indigo-700"
                >
                  academiahub2025@gmail.com
                </a>
              </div>
              <div className="text-center p-8 border border-gray-200 rounded-lg">
                <Phone className="h-8 w-8 mx-auto mb-4 text-indigo-600" />
                <h3 className="font-semibold mb-2">Phone Support</h3>
                <p className="text-gray-600 mb-4">
                  Available Mon-Fri, 9am-5pm EST
                </p>
                <a
                  href="tel:+1234567890"
                  className="text-indigo-600 hover:text-indigo-700"
                >
                  +92 310 4941344
                </a>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default Help;
